import React, { useEffect, useState, useMemo, useContext } from "react";
import {
  TextField,
  Button,
  Modal,
  Backdrop,
  Fade,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CreateIcon from "@mui/icons-material/Create";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";

import Swal from "sweetalert2";

import moment from "moment";

import DataTable from "react-data-table-component";
import Toast from "../../utils/Toast";

import useGetPromotions from "../../queries/promotion/getPromotions";
import useGetTransactionWPromo from "../../queries/transaction/getTransactionWPromo";

import { AuthContext } from "../../utils/userProvider";
import useCreateAudit from "../../mutation/audit/createAudit";
import createAudit from "../../mutation/audit/createAuditNoHook";

import Loader from "../../components/Loader";
import {
  ListPageShell,
  ListPageCard,
  ListPageHeader,
  dataTableCustomStyles,
  modalSx,
  DeleteModalFrame,
} from "../../components/ListPage";

const Promotions = () => {
  const [promotionTable, setPromotionTable] = useState([]);
  const [transactionTable, setTransactionTable] = useState([]);
  const { authState, setAuthState } = useContext(AuthContext);

  const [openDeletePromotion, setOpenDeletePromotion] = useState(false);
  const [deleteRow, setDeleteRow] = useState();

  const [filterText, setFilterText] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);

  // Generate available years (last 5 years and current year)
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 6 }, (_, i) => currentYear - i);
    setAvailableYears(years);
  }, []);

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  const typeToText = (type) => {
    let text = "";
    switch (type) {
      case "promotional_period":
        text = "Promotional Period";
        break;
      case "weekly_rent_rebate":
        text = "Weekly Rent Rebate";
        break;
      case "rent_free_promo":
        text = "Rent Free Promo";
        break;
      case "hk_prop_promo":
        text = "HK Rent Free Promo";
        break;
      case "hk_rate_override":
        text = "HK Rate Override";
        break;
      default:
        break;
    }
    return text;
  };

  let columns = [
    {
      name: "Name",
      selector: "name",
      sortable: true,
    },
    {
      name: "Code",
      selector: "code",
      sortable: true,
    },
    {
      name: "Type",
      selector: "type",
      sortable: true,
      cell: (row, index, column, id) => {
        let text = typeToText(row.type);

        return <span>{text}</span>;
      },
    },
    {
      name: "Start Date",
      selector: "startDate",
      sortable: true,
      cell: (row, index, column, id) => {
        return <span>{moment(row.startDate).format("DD MMM YYYY")}</span>;
      },
    },
    {
      name: "End Date",
      selector: "endDate",
      sortable: true,
      cell: (row, index, column, id) => {
        return <span>{moment(row.endDate).format("DD MMM YYYY")}</span>;
      },
    },
    {
      name: "Redemption Count",
      selector: "redemptionCount",
      sortable: false,
      cell: (row, index, column, id) => {
        if (row.redemptionCount) {
          return <span>{row.redemptionCount}</span>;
        } else {
          return <span>0</span>;
        }
      },
    },

    {
      name: "Active",
      sortable: false,
      cell: (row, index, column, id) => {
        if (!row.archived) {
          if (authState.editAccess) {
            return (
              <Switch
                defaultChecked={row.active}
                onChange={(event) => {
                  handleActiveChange(event, row);
                }}
                disabled={!authState.editAccess}
                name="checkedA"
                inputProps={{ "aria-label": "secondary checkbox" }}
              />
            );
          } else {
            return (
              <span>
                {row.active && "Yes"}
                {!row.active && "No"}
              </span>
            );
          }
        }
      },
    },
    {
      name: "Auto Applies?",
      selector: "autoApply",
      sortable: false,
      cell: (row, index, column, id) => {
        if (!row.archived) {
          if (authState.editAccess) {
            return (
              <Switch
                defaultChecked={row.autoApply}
                onChange={(event) => {
                  handleAutoApplyChange(event, row);
                }}
                disabled={!authState.editAccess}
                name="autoApply"
                inputProps={{ "aria-label": "secondary checkbox" }}
              />
            );
          } else {
            return (
              <span>
                {row.autoApply && "Yes"}
                {!row.autoApply && "No"}
              </span>
            );
          }
        }
      },
    },
    {
      name: "Archived",
      selector: "archived",
      sortable: true,
      cell: (row, index, column, id) => {
        let text = "No";
        if (row.archived) {
          text = "Yes";
        }
        return <span>{text}</span>;
      },
    },
    {
      name: "Options",
      cell: (row, index, column, id) => {
        if (authState.editAccess) {
          if (!row.archived) {
            return (
              <div>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<CreateIcon />}
                  onClick={() => {
                    editItem(row);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    deleteItem(row);
                  }}
                >
                  Delete
                </Button>
              </div>
            );
          } else {
            return null;
          }
        } else {
          if (!row.archived) {
            return (
              <div>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<VisibilityIcon />}
                  onClick={() => {
                    editItem(row);
                  }}
                >
                  View
                </Button>
              </div>
            );
          }
        }
      },
    },
  ];

  const [promotionResponse, resetPromotion] = useGetPromotions(selectedYear);
  const [transactionResponse, resetTransaction] = useGetTransactionWPromo();
  const [auditResponse, setAuditData] = useCreateAudit();

  const handleFilterText = (event) => {
    setFilterText(event.target.value);
  };

  const filteredItems = promotionTable.filter(
    (item) =>
      (item.name &&
        item.name.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.code &&
        item.code.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.type &&
        typeToText(item.type)
          .toLowerCase()
          .includes(filterText.toLowerCase())) ||
      (item.startDate &&
        moment(item.startDate)
          .format("DD MMM YYYY")
          .toLowerCase()
          .includes(filterText.toLowerCase())) ||
      (item.endDate &&
        moment(item.endDate)
          .format("DD MMM YYYY")
          .toLowerCase()
          .includes(filterText.toLowerCase()))
  );

  const handleAutoApplyChange = (event, row) => {
    let updateObject = {
      id: row.id,
      autoApply: event.target.checked,
    };
    let auditObject = {
      newData: updateObject,
      snapshotCurrent: row,
      currentData: row.id,
      type: "promotion",
      action: "update",
    };
    Swal.fire({
      title: "Create Updates",
      html: `<h4>Please provide a description before submitting your changes</h4>
      <textarea maxlength="100" type="text" id="actionDescription" class="swal2-input" placeholder="Description" font-size=18px style="height:auto!important" rows=5>`,
      confirmButtonText: "Confirm",
      preConfirm: () => {
        const actionDescription =
          Swal.getPopup().querySelector("#actionDescription").value;
        if (!actionDescription) {
          Swal.showValidationMessage(`Please enter description`);
        }
        auditObject.actionDescription = actionDescription;
        return auditObject;
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        await createAudit(result.value, authState ? authState.jwt : "")
          .then((response) => {
            const Toast = Swal.mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
              didOpen: (toast) => {
                toast.addEventListener("mouseenter", Swal.stopTimer);
                toast.addEventListener("mouseleave", Swal.resumeTimer);
              },
            });
            Toast.fire({
              icon: "success",
              title: "Auto Apply Update Audit Created",
            });
            updateTableData(response.data);
          })
          .catch((err) => {
            Swal.fire({
              icon: "error",
              titleText: "Error",
              text: "An Error has occured. Please try again.",
              allowOutsideClick: true,
              allowEscapeKey: true,
            });
            updateTableData();
          });
      } else {
        updateTableData();
      }
    });
  };

  const handleActiveChange = (event, row) => {
    console.log(event.target.checked);
    let updateObject = {
      id: row.id,
      active: event.target.checked,
    };
    let auditObject = {
      newData: updateObject,
      snapshotCurrent: row,
      currentData: row.id,
      type: "promotion",
      action: "update",
    };
    Swal.fire({
      title: "Create Updates",
      html: `<h4>Please provide a description before submitting your changes</h4>
      <textarea maxlength="100" type="text" id="actionDescription" class="swal2-input" placeholder="Description" font-size=18px style="height:auto!important" rows=5>`,
      confirmButtonText: "Confirm",
      preConfirm: () => {
        const actionDescription =
          Swal.getPopup().querySelector("#actionDescription").value;
        if (!actionDescription) {
          Swal.showValidationMessage(`Please enter description`);
        }
        auditObject.actionDescription = actionDescription;
        return auditObject;
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        await createAudit(result.value, authState ? authState.jwt : "")
          .then((response) => {
            const Toast = Swal.mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
              didOpen: (toast) => {
                toast.addEventListener("mouseenter", Swal.stopTimer);
                toast.addEventListener("mouseleave", Swal.resumeTimer);
              },
            });
            Toast.fire({
              icon: "success",
              title: "Active Update Audit Created",
            });
            updateTableData(response.data);
          })
          .catch((err) => {
            Swal.fire({
              icon: "error",
              titleText: "Error",
              text: "An Error has occured. Please try again.",
              allowOutsideClick: true,
              allowEscapeKey: true,
            });
            updateTableData();
          });
      } else {
        updateTableData();
      }
    });
  };

  const updateTableData = (data) => {
    let newTableData = [...promotionTable];
    if (data != null) {
      let index = newTableData.findIndex((object) => object.id == data.id);
      newTableData[index] = data;
      setPromotionTable(newTableData);
    } else {
      setPromotionTable(newTableData);
    }
  };

  /* useEffect(() => {
    if (wordpressResponse.data) {
      setWordpressTable([...wordpressResponse.data]);
    }
  }, [wordpressResponse]); */

  useEffect(() => {
    if (promotionResponse.data) {
      setPromotionTable([...promotionResponse.data]);
    }
  }, [promotionResponse]);

  useEffect(() => {
    if (transactionResponse.data) {
      setTransactionTable([...transactionResponse.data]);
    }
  }, [transactionResponse]);

  let [transactionSet, setTransactionSet] = useState(false);

  useEffect(() => {
    if (
      !transactionSet &&
      promotionTable.length > 0 &&
      transactionTable.length > 0
    ) {
      let newPromotionTable = [...promotionTable];
      for (var i = 0; i < promotionTable.length; i++) {
        let promotion = promotionTable[i];
        let transactionCount = 0;
        for (var a = 0; a < transactionTable.length; a++) {
          let transaction = transactionTable[a];
          if (transaction?.promotionApplied?.id == promotion.id) {
            transactionCount++;
          }
        }
        newPromotionTable[i].redemptionCount = transactionCount;
      }
      console.log(newPromotionTable);
      setPromotionTable([...newPromotionTable]);
      setTransactionSet(true);
    }
  }, [promotionTable, transactionTable, transactionSet]);

  const editItem = (row) => {
    if (authState.editAccess) {
      window.location.href = "/dashboard/promotions/edit/" + row.id;
    } else {
      window.location.href = "/dashboard/promotions/view/" + row.id;
    }
  };

  const deleteItem = (row) => {
    setDeleteRow(row);
    setOpenDeletePromotion(true);
  };

  const handleClose = () => {
    setOpenDeletePromotion(false);
  };

  const confirmDelete = () => {
    Swal.fire({
      title: "Create Updates",
      html: `<h4>Please provide a description before submitting your changes</h4>
      <textarea maxlength="100" type="text" id="actionDescription" class="swal2-input" placeholder="Description" font-size=18px style="height:auto!important" rows=5>`,
      confirmButtonText: "Confirm",
      focusConfirm: false,
      preConfirm: () => {
        const actionDescription =
          Swal.getPopup().querySelector("#actionDescription").value;
        if (!actionDescription) {
          Swal.showValidationMessage(`Please enter description`);
        }
        return {
          actionDescription: actionDescription,
          snapshotCurrent: deleteRow,
          currentData: deleteRow.id,
          type: "promotion",
          action: "delete",
        };
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setAuditData(result.value);
        console.error(result.value);
      }
    });
  };

  useEffect(() => {
    if (auditResponse.data) {
      Toast.fire({
        icon: "success",
        title: "Promotion deletion Audit Created",
      });
      setDeleteRow(null);
      setOpenDeletePromotion(false);
      resetPromotion();
    } else if (auditResponse.error) {
      Toast.fire({
        icon: "error",
        text: "An Error has occured. Please try again.",
      });
      setDeleteRow(null);
      setOpenDeletePromotion(false);
    }
  }, [auditResponse]);

  const newPromotion = () => {
    window.location.href = "/dashboard/promotions/new";
  };

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <div style={{ display: "flex", gap: "10px" }}>
          {authState.role == "Executive" && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={newPromotion}
            >
              New Promotion
            </Button>
          )}
          <FormControl
            variant="outlined"
            size="small"
            style={{ minWidth: 120 }}
          >
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              onChange={handleYearChange}
              label="Year"
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            id="filter-text"
            onChange={handleFilterText}
            label="Search"
            variant="outlined"
            size="small"
          />
        </div>
      </div>
    );
  }, [filterText, availableYears, selectedYear]);

  return (
    <ListPageShell>
      <ListPageCard>
        {promotionResponse.loading && <Loader />}
        {!promotionResponse.loading && (
          <div>
            <ListPageHeader title="Promotions" />
            <DataTable
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10]}
              noHeader={true}
              subHeader
              data={filteredItems}
              columns={columns}
              subHeaderComponent={subHeaderComponentMemo}
              customStyles={dataTableCustomStyles}
            />
          </div>
        )}
      </ListPageCard>
      <Modal
        aria-labelledby="delete-modal"
        aria-describedby="delete-modal-description"
        open={openDeletePromotion}
        onClose={(event, reason) => {
          if (
            auditResponse.loading &&
            (reason === "backdropClick" || reason === "escapeKeyDown")
          ) {
            return;
          }
          handleClose();
        }}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
        disableEnforceFocus
        sx={modalSx}
      >
        <Fade in={openDeletePromotion}>
          <Box>
            <DeleteModalFrame title="Delete Promotion">
              {deleteRow && (
                <div>
                  <p id="delete-modal-description">
                    Are you sure you want to delete{" "}
                    <b>
                      <u>{deleteRow.name}</u>
                    </b>
                    ?
                  </p>
                  <p>
                    This action cannot be{" "}
                    <b>
                      <u>reversed</u>
                    </b>
                    .
                  </p>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 5,
                      gap: 1,
                    }}
                  >
                    <Button
                      onClick={confirmDelete}
                      variant="contained"
                      color="primary"
                      disabled={auditResponse.loading}
                    >
                      Confirm
                    </Button>
                    <Button
                      onClick={handleClose}
                      variant="contained"
                      color="secondary"
                      disabled={auditResponse.loading}
                    >
                      Cancel
                    </Button>
                  </Box>
                </div>
              )}
            </DeleteModalFrame>
          </Box>
        </Fade>
      </Modal>
    </ListPageShell>
  );
};

export default Promotions;
